// Sunday-anchored week key helpers for member court reservations.
//
// A "week" runs Sunday 00:00 through Saturday 23:59 local club time.
// Weekly membership hour allocations reset at Sunday midnight.
//
// weekKey format: "YYYY-WNN" where NN is the week-of-year of the Sunday
// that starts the week (2-digit, Sunday-anchored — NOT ISO weeks, which
// are Monday-anchored).
//
// All functions take a local date string "YYYY-MM-DD" (as produced by
// utils/dates.js dateStr()/localToday()) so no UTC parsing traps apply —
// we append T12:00:00 before constructing Date objects, which parses as
// local time and keeps us safely away from midnight boundary issues.

/** Parse a YYYY-MM-DD string into a local-time Date at noon (UTC-trap safe). */
function toLocalNoon(dateStr) {
  return new Date(`${dateStr}T12:00:00`);
}

/** Format a Date as YYYY-MM-DD using local time components. */
function fmt(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * The Sunday (YYYY-MM-DD) that starts the week containing dateStr.
 */
export function getWeekStart(dateStr) {
  const d = toLocalNoon(dateStr);
  const sunday = new Date(d);
  sunday.setDate(d.getDate() - d.getDay()); // getDay(): 0 = Sunday
  return fmt(sunday);
}

/**
 * The Saturday (YYYY-MM-DD) that ends the week containing dateStr.
 */
export function getWeekEnd(dateStr) {
  const d = toLocalNoon(dateStr);
  const saturday = new Date(d);
  saturday.setDate(d.getDate() + (6 - d.getDay()));
  return fmt(saturday);
}

/**
 * Sunday-anchored week key "YYYY-WNN" for the week containing dateStr.
 *
 * The year is the year of the week's Sunday. Week 1 is the week containing
 * Jan 1 of that year (Sunday-anchored). This is a bucketing key, not an ISO
 * week — the only requirement is that every date within one Sun–Sat window
 * maps to the same key, and keys sort chronologically within a year.
 */
export function getWeekKey(dateStr) {
  const sundayStr = getWeekStart(dateStr);
  const sunday = toLocalNoon(sundayStr);
  const y = sunday.getFullYear();

  // Sunday that starts the week containing Jan 1 of the Sunday's year
  const jan1 = new Date(y, 0, 1, 12, 0, 0);
  const firstSunday = new Date(jan1);
  firstSunday.setDate(jan1.getDate() - jan1.getDay());

  const MS_PER_DAY = 86400000;
  const weekNum = Math.floor((sunday - firstSunday) / (7 * MS_PER_DAY)) + 1;

  return `${y}-W${String(weekNum).padStart(2, '0')}`;
}

/**
 * Convenience: memberUsage doc ID for a member + date.
 */
export function usageDocId(memberId, dateStr) {
  return `${memberId}_${getWeekKey(dateStr)}`;
}
